// src/cockpit4/components/IntegrationMarketPanel.tsx
// ============================================
// 第三方集成（设置中心 P1-功能5 深度扩展）
// 企业微信/钉钉/飞书/邮件/短信/对象存储/ERP/CRM 等集成卡片，
// 连接/断开热插拔（立即生效），按分类浏览，集成市场模式。
// 数据来源：integrationConfig（localStorage，subscribe 热更新）
// ============================================

import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  PlugZap, MessageSquare, Bell, Send, Mail, Smartphone,
  Cloud, Database, Headset, Notebook, Search, Check, Power, X, Plug,
} from "lucide-react";
import { integrationConfig } from "../../services/integrationConfig";
import type { IntegrationChannel } from "../../services/integrationConfig";

const CATEGORY_META: Record<string, { label: string; color: string }> = {
  im: { label: "即时通讯", color: "#38BDF8" },
  notify: { label: "通知渠道", color: "#FBBF24" },
  storage: { label: "对象存储", color: "#34D399" },
  erp: { label: "ERP 系统", color: "#A78BFA" },
  crm: { label: "客户系统", color: "#F472B6" },
  knowledge: { label: "知识工具", color: "#FB923C" },
};

const ICON_MAP: Record<string, any> = {
  MessageSquare, Bell, Send, Mail, Smartphone, Cloud, Database, Headset, Notebook, Plug,
};

const CONFIG_FIELDS: Record<string, Array<{ key: string; label: string; placeholder: string; secret?: boolean }>> = {
  wechat_work: [
    { key: "corpId", label: "企业 ID（CorpID）", placeholder: "wwxxxxxxxx" },
    { key: "agentId", label: "应用 AgentId", placeholder: "1000002" },
    { key: "corpSecret", label: "应用 Secret", placeholder: "输入应用密钥", secret: true },
  ],
  dingtalk: [
    { key: "webhookUrl", label: "群机器人 Webhook", placeholder: "https://oapi.dingtalk.com/robot/send?access_token=..." },
    { key: "secret", label: "加签密钥", placeholder: "SEC...", secret: true },
  ],
  feishu: [
    { key: "appId", label: "App ID", placeholder: "cli_xxx" },
    { key: "appSecret", label: "App Secret", placeholder: "输入应用密钥", secret: true },
    { key: "webhookUrl", label: "机器人 Webhook", placeholder: "https://open.feishu.cn/open-apis/bot/v2/hook/..." },
  ],
  email_smtp: [
    { key: "host", label: "SMTP 服务器", placeholder: "smtp.qq.com" },
    { key: "port", label: "端口", placeholder: "465" },
    { key: "username", label: "发件邮箱", placeholder: "admin@xshetech.com" },
    { key: "password", label: "授权码", placeholder: "输入授权码", secret: true },
  ],
  sms_aliyun: [
    { key: "accessKeyId", label: "AccessKey ID", placeholder: "LTAI..." },
    { key: "accessKeySecret", label: "AccessKey Secret", placeholder: "输入密钥", secret: true },
    { key: "signName", label: "短信签名", placeholder: "小舍科技" },
  ],
  storage_oss: [
    { key: "accessKeyId", label: "AccessKey ID", placeholder: "LTAI..." },
    { key: "accessKeySecret", label: "AccessKey Secret", placeholder: "输入密钥", secret: true },
    { key: "bucket", label: "Bucket 名称", placeholder: "xeaos-media" },
    { key: "region", label: "Region", placeholder: "oss-cn-hangzhou" },
  ],
  storage_qiniu: [
    { key: "accessKey", label: "AccessKey", placeholder: "输入 AccessKey" },
    { key: "secretKey", label: "SecretKey", placeholder: "输入密钥", secret: true },
    { key: "bucket", label: "Bucket 名称", placeholder: "xeaos" },
  ],
  storage_upyun: [
    { key: "operator", label: "操作员", placeholder: "操作员名" },
    { key: "password", label: "操作员密码", placeholder: "输入密码", secret: true },
    { key: "bucket", label: "Bucket 名称", placeholder: "xeaos" },
  ],
  erp_jindie: [
    { key: "appId", label: "应用 ID", placeholder: "输入金蝶开放平台应用 ID" },
    { key: "appSecret", label: "应用 Secret", placeholder: "输入密钥", secret: true },
  ],
  erp_yonyou: [
    { key: "apiKey", label: "API Key", placeholder: "输入用友 API Key", secret: true },
    { key: "tenantId", label: "租户 ID", placeholder: "输入租户 ID" },
  ],
  crm_wechat: [
    { key: "corpId", label: "企业 ID", placeholder: "wwxxxxxxxx" },
    { key: "secret", label: "Secret", placeholder: "输入密钥", secret: true },
  ],
  notion: [
    { key: "token", label: "Internal Token", placeholder: "secret_xxx", secret: true },
    { key: "pageId", label: "根页面 ID", placeholder: "输入页面 ID" },
  ],
};

export default function IntegrationMarketPanel() {
  const [channels, setChannels] = useState<IntegrationChannel[]>(integrationConfig.getIntegrations());
  const [filter, setFilter] = useState<string>("all");
  const [query, setQuery] = useState("");
  const [connecting, setConnecting] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    const unsub = integrationConfig.subscribe(() => {
      setChannels(integrationConfig.getIntegrations());
    });
    return unsub;
  }, []);

  const connectedCount = useMemo(() => channels.filter((c) => c.connected).length, [channels]);
  const categories = useMemo(() => {
    const set = new Set(channels.map((c) => c.category));
    return ["all", ...Array.from(set)];
  }, [channels]);

  const filtered = useMemo(() => {
    return channels.filter((c) => {
      if (filter !== "all" && c.category !== filter) return false;
      if (query && !(c.name + c.description).includes(query)) return false;
      return true;
    });
  }, [channels, filter, query]);

  const doConnect = (id: string) => {
    integrationConfig.connectIntegration(id, draft);
    setConnecting(null);
    setDraft({});
    setFlash(`${integrationConfig.getIntegration(id)?.name} 已连接（热插拔生效）`);
    setTimeout(() => setFlash(null), 3000);
  };

  const connectingChannel = connecting ? channels.find((c) => c.id === connecting) : null;
  const fields = connectingChannel ? CONFIG_FIELDS[connectingChannel.id] || [] : [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <PlugZap size={14} className="text-emerald-400" />
        <h3 className="text-[12px] font-medium text-white">第三方集成</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P1-功能5 · Integration Market</span>
        <div className="ml-auto flex items-center gap-1 text-[9px] font-mono text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> 热插拔已启用
        </div>
      </div>

      {flash && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-2 text-[10px] text-emerald-300">
          {flash}
        </motion.div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-1.5">
          <span className="text-[10px] font-mono text-gray-400">已连接</span>
          <span className="text-sm font-semibold text-emerald-400">{connectedCount}/{channels.length}</span>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {categories.map((c) => (
            <button key={c} onClick={() => setFilter(c)}
              className={`px-2 py-1 rounded-md text-[10px] ${filter === c ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "text-gray-400 border border-white/[0.06] hover:text-white"}`}>
              {c === "all" ? "全部" : CATEGORY_META[c]?.label || c}
            </button>
          ))}
        </div>
        <div className="ml-auto relative">
          <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-600" />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索集成…"
            className="pl-7 pr-2 py-1.5 w-40 rounded-md bg-black/30 border border-white/[0.08] text-[11px] text-white placeholder-gray-600 outline-none focus:border-emerald-500/40" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {filtered.map((c) => {
          const Icon = ICON_MAP[c.icon] || Plug;
          const meta = CATEGORY_META[c.category] || { label: c.category, color: "#888" };
          return (
            <div key={c.id} className={`rounded-lg border p-3 transition-colors ${c.connected ? "border-emerald-500/25 bg-emerald-500/[0.05]" : "border-white/[0.06] bg-white/[0.02] hover:border-white/[0.15]"}`}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${meta.color}1a`, border: `1px solid ${meta.color}33` }}>
                  <Icon size={13} style={{ color: meta.color }} />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] font-medium text-white truncate">{c.name}</div>
                  <div className="text-[9px] font-mono text-gray-500">{meta.label}</div>
                </div>
                <span className={`ml-auto w-1.5 h-1.5 rounded-full ${c.connected ? "bg-emerald-400" : "bg-gray-600"}`} />
              </div>
              <p className="mt-1.5 text-[9px] text-gray-500 leading-relaxed line-clamp-2">{c.description}</p>
              <div className="mt-2 flex items-center gap-1.5">
                {c.connected ? (
                  <>
                    <span className="text-[9px] font-mono text-emerald-400">
                      {c.connectedAt ? `已连接 ${new Date(c.connectedAt).toLocaleDateString()}` : "已连接"}
                    </span>
                    <button onClick={() => { integrationConfig.disconnectIntegration(c.id); setFlash(`${c.name} 已断开`); setTimeout(() => setFlash(null), 2500); }}
                      className="ml-auto flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/10 border border-red-500/25 text-[9px] text-red-400 hover:bg-red-500/20">
                      <Power size={9} /> 断开
                    </button>
                  </>
                ) : (
                  <button onClick={() => setConnecting(c.id)}
                    className="ml-auto flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/25 text-[9px] text-emerald-300 hover:bg-emerald-500/20">
                    <Plug size={9} /> 连接
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {connectingChannel && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setConnecting(null)}>
          <div className="w-full max-w-md rounded-xl border border-white/[0.1] bg-[#0b0e13] p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-md flex items-center justify-center bg-emerald-500/15 border border-emerald-500/30">
                <PlugZap size={13} className="text-emerald-400" />
              </div>
              <div>
                <div className="text-[12px] font-medium text-white">连接 {connectingChannel.name}</div>
                <div className="text-[9px] font-mono text-gray-500">配置后立即生效 · 热插拔</div>
              </div>
              <button onClick={() => setConnecting(null)} className="ml-auto text-gray-500 hover:text-white"><X size={14} /></button>
            </div>
            <div className="space-y-2">
              {fields.length === 0 && (
                <div className="text-[10px] text-gray-500">该集成无需额外配置，直接连接。</div>
              )}
              {fields.map((f) => (
                <div key={f.key}>
                  <label className="text-[9px] font-mono text-gray-400 uppercase">{f.label}</label>
                  <input
                    type={f.secret ? "password" : "text"}
                    value={draft[f.key] || ""}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="mt-0.5 w-full px-2 py-1.5 rounded-md bg-black/30 border border-white/[0.08] text-[11px] text-white placeholder-gray-600 outline-none focus:border-emerald-500/40" />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setConnecting(null)}
                className="flex-1 py-1.5 rounded-md border border-white/[0.08] text-[10px] text-gray-400 hover:text-white">取消</button>
              <button onClick={() => doConnect(connectingChannel.id)}
                className="flex-1 py-1.5 rounded-md bg-emerald-500/20 border border-emerald-500/30 text-[10px] text-emerald-300 hover:bg-emerald-500/30 flex items-center justify-center gap-1">
                <Check size={10} /> 确认连接
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

